import { FC } from 'react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Loading } from '@components/atoms/Loading';
import MinVersionForm from '@pages/settings/components/MinVersionForm';
import {
  useCurrentUser,
  useSettings,
  useCreateSetting,
  useUpdateSetting,
} from '@hooks/api';
import { useTranslation } from '@hooks/useTranslation';

const SettingsPage: FC = () => {
  const tSettings = useTranslation('settings');
  const { mutateAsync: createSetting, isPending: isCreatingSetting } =
    useCreateSetting();
  const { mutateAsync: updateSetting, isPending: isUpdatingSetting } =
    useUpdateSetting();
  const { isLoading: isLoadingCurrentUser } = useCurrentUser();
  const { data: settingsData, isLoading: isLoadingSettings } = useSettings();

  const minVersionSetting = settingsData?.settings?.find(
    (s) => s.name === 'minVersion',
  );

  const isLoading =
    isLoadingCurrentUser ||
    isCreatingSetting ||
    isUpdatingSetting ||
    isLoadingSettings;

  const versionRegex = /^\d{1,2}(\.\d{1,2}){2}$/;

  type MinVersionFormValues = { minVersion: string; description: string };
  const minVersionValue: string = minVersionSetting?.value || '';
  const minVersionDescription: string = minVersionSetting?.description || '';

  const minVersionFormik = useFormik<MinVersionFormValues>({
    enableReinitialize: true,
    initialValues: {
      minVersion: minVersionValue,
      description: minVersionDescription,
    },
    validationSchema: Yup.object({
      minVersion: Yup.string()
        .matches(versionRegex, 'Use format: X.Y.Z')
        .required('Minimum version is required'),
      description: Yup.string().optional(),
    }),
    onSubmit: async (values) => {
      if (minVersionSetting?.settingId) {
        // Update existing setting
        await updateSetting({
          settingId: minVersionSetting.settingId,
          input: {
            value: values.minVersion,
            description: values.description,
          },
        });
      } else {
        // Create new setting
        await createSetting({
          name: 'minVersion',
          value: values.minVersion,
          description: values.description,
        });
      }
    },
  });

  if (isLoading) {
    return <Loading center size="2xl" />;
  }

  return (
    <div className="h-screen">
      <div className="flex items-center gap-2">
        <AccountCircleOutlinedIcon
          sx={{ fontSize: 28 }}
          className="text-orange-400 dark:text-orange-300"
        />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {tSettings.settingsTitle}
        </h1>
      </div>

      <MinVersionForm
        loading={isLoadingSettings}
        versionRegex={versionRegex}
        settingExists={!!minVersionSetting}
        handleBlur={minVersionFormik.handleBlur}
        isSubmitting={minVersionFormik.isSubmitting}
        handleSubmit={minVersionFormik.handleSubmit}
        handleChange={minVersionFormik.handleChange}
        value={minVersionFormik.values.minVersion}
        description={minVersionFormik.values.description}
        error={minVersionFormik.errors.minVersion}
        descriptionError={minVersionFormik.errors.description}
        touched={minVersionFormik.touched.minVersion}
        descriptionTouched={minVersionFormik.touched.description}
      />
    </div>
  );
};

export default SettingsPage;
